import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useVideoAnimate } from '@genfun/core/hooks/useVideoAnimate';
import { fetchGallery, fetchRemixData } from '@genfun/core/api/API';
import {
    BENTO_FIRST_PAGE, BENTO_PAGE, LIVE_TEMPLATES, VIDEO_TEMPLATES,
    filterTemplates, paginate, resolveMedia, tagsFor, templateById,
} from '../data/videoTemplates';
import { useSession } from '../utils/session';
import { isSoundOwner } from '../utils/soundBus';
import { useInViewport } from '../utils/useInViewport';
import { useMediaQuery } from '../utils/useMediaQuery';
import { downloadFile } from '../utils/download';
import { TemplateCard } from '../components/TemplateCard';
import { Pager } from '../components/Pager';
import { OfferCard } from '../components/OfferCard';
import { HeroDrop } from '../components/HeroDrop';
import { GenerationProgress } from '../components/GenerationProgress';
import { GalleryStrip } from '../components/GalleryStrip';
import { Icon } from '../components/Icon';
import { TemplatePreviewPopup } from '../popups/TemplatePreviewPopup';
import { TemplatePickerPopup } from '../popups/TemplatePickerPopup';
import { ImagePickerPopup } from '../popups/ImagePickerPopup';
import './Video.css';

const RENDER_MS = 120000;
const RENDER_MESSAGES = ['Creating magic', 'Rendering frames', 'Polishing details', 'Almost there'];

const preloadPosters = (list) => list.forEach((t) => { new Image().src = resolveMedia(t).poster; });

// The 2x1 cell of the bento. Same footprint for everyone, so page maths never depends on auth:
// visitors get the price tag, subscribers a shortcut to the scenes that already generate.
function BentoSlot({ paid, pending, order, onBrowseLive }) {
    if (pending) return <div className="vidSlot vidSlot--pending skeleton" aria-hidden="true" />;
    if (!paid) return <OfferCard variant="card" order={order} className="vidSlot" />;
    return (
        <button type="button" className="vidSlot vidReady glass" style={{ '--i': order }} onClick={onBrowseLive}>
            <span className="vidCreateIcon"><Icon name="play" size={22} /></span>
            <span className="vidCreateText">
                <b>{LIVE_TEMPLATES.length} scenes ready to generate</b>
                <span>Skip the previews — these make your video right now.</span>
            </span>
            <span className="btn btn--primary">Show them <Icon name="arrowRight" size={15} strokeWidth={2.4} /></span>
        </button>
    );
}

function TemplateBrowser({ onOpen, paid, subPending, gridRef }) {
    const [params, setParams] = useSearchParams();
    const tags = tagsFor(paid);
    const tag = tags.some((t) => t.key === params.get('tag')) ? params.get('tag') : 'all';
    const list = useMemo(() => filterTemplates(tag), [tag]);
    // The unfiltered view is a bento on every page (see BENTO_* in data/videoTemplates); filters keep the plain grid.
    const pageOpts = useMemo(() => (tag === 'all' ? { firstPageSize: BENTO_FIRST_PAGE, size: BENTO_PAGE } : {}), [tag]);
    const view = paginate(list, params.get('page'), pageOpts);
    const bento = tag === 'all' && view.items.length >= 5;
    const withSlot = bento && view.page === 1; // the price tag / shortcut cell lives on page 1 only
    const base = view.from - 1; // chip numbers when phones reorder the first cards
    const phone = useMediaQuery('(max-width: 720px)');
    // Filtered views keep the price in sight too: a full-width strip after the first four cards.
    const offerBarAt = !bento && !paid && !subPending && view.page === 1 ? Math.min(4, view.items.length) : -1;

    const update = useCallback((patch) => {
        const next = new URLSearchParams(params);
        Object.entries(patch).forEach(([k, v]) => (v ? next.set(k, v) : next.delete(k)));
        setParams(next);
    }, [params, setParams]);

    const goToPage = useCallback((n) => {
        if (n < 1 || n > view.pages || n === view.page) return;
        update({ page: n > 1 ? String(n) : null });
    }, [update, view.page, view.pages]);

    // Every page change starts at the top of the grid. Done after the new page has rendered and
    // against the stable wrapper: the grid itself is re-created per page, which used to kill the scroll.
    const firstRender = useRef(true);
    useEffect(() => {
        if (firstRender.current) { firstRender.current = false; return undefined; }
        const still = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
        const id = requestAnimationFrame(() => gridRef.current?.scrollIntoView({ behavior: still ? 'auto' : 'smooth', block: 'start' }));
        return () => cancelAnimationFrame(id);
    }, [view.page, gridRef]); // not on a filter tap: the chips sit right above the grid and would scroll away

    const prefetchPage = useCallback((n) => preloadPosters(paginate(list, n, pageOpts).items), [list, pageOpts]);

    // Warm the next page's posters once the current one has settled.
    useEffect(() => {
        if (view.page >= view.pages) return undefined;
        const t = setTimeout(() => prefetchPage(view.page + 1), 1500);
        return () => clearTimeout(t);
    }, [view.page, view.pages, prefetchPage]);

    useEffect(() => {
        const onKey = (e) => {
            if (e.target.closest?.('input, textarea') || document.querySelector('.modalOverlay, .lightbox')) return;
            if (e.key === 'ArrowRight') goToPage(view.page + 1);
            else if (e.key === 'ArrowLeft') goToPage(view.page - 1);
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [goToPage, view.page]);

    return (
        <>
            <div className="vidChips" role="tablist" aria-label="Filter scenes">
                {tags.map((t) => (
                    <button
                        key={t.key}
                        type="button"
                        role="tab"
                        aria-selected={t.key === tag}
                        className={`chip${t.key === tag ? ' chip--active' : ''}`}
                        onClick={() => update({ tag: t.key === 'all' ? null : t.key, page: null })}
                    >
                        {t.label}<span className="chipCount">{filterTemplates(t.key).length}</span>
                    </button>
                ))}
            </div>

            <div className="vidGridAnchor" ref={gridRef}>
            <div
                className={`vidGrid${bento ? ' vidGrid--bento' : ''}${offerBarAt >= 0 ? ' vidGrid--offer' : ''}`}
                key={`${tag}:${view.page}`}
            >
                {bento ? (
                    <>
                        {/* Phones open with two small scenes and then the big one; the numbers follow what is on screen. */}
                        {phone && view.items.slice(1, 3).map((t, i) => <TemplateCard key={t.id} template={t} order={i} index={base + i + 1} onOpen={onOpen} paid={paid} />)}
                        <TemplateCard key={view.items[0].id} template={view.items[0]} order={phone ? 2 : 0} index={phone ? base + 3 : undefined} onOpen={onOpen} paid={paid} featured />
                        {view.items.slice(phone ? 3 : 1, 5).map((t, i) => <TemplateCard key={t.id} template={t} order={i + (phone ? 3 : 1)} onOpen={onOpen} paid={paid} />)}
                        {withSlot && <BentoSlot paid={paid} pending={subPending} order={5} onBrowseLive={() => update({ tag: 'live', page: null })} />}
                        {view.items.slice(5).map((t, i) => <TemplateCard key={t.id} template={t} order={i + 6} onOpen={onOpen} paid={paid} />)}
                    </>
                ) : (
                    view.items.map((t, i) => [
                        i === offerBarAt && <OfferCard key="offer" variant="bar" order={i} />,
                        <TemplateCard key={t.id} template={t} order={i < offerBarAt || offerBarAt < 0 ? i : i + 1} onOpen={onOpen} paid={paid} />,
                    ])
                )}
                {offerBarAt >= 0 && offerBarAt === view.items.length && <OfferCard variant="bar" order={offerBarAt} />}

                {view.page === view.pages && (
                    <Link to="/photo" className="vidCreateTile glass">
                        <span className="vidCreateIcon"><Icon name="sparkle" size={24} /></span>
                        <span className="vidCreateText">
                            <b>Don&apos;t see your scene?</b>
                            <span>Create your own girl in Photo, then bring her into any video.</span>
                        </span>
                        <span className="btn btn--primary">Create a photo <Icon name="arrowRight" size={15} strokeWidth={2.4} /></span>
                    </Link>
                )}
            </div>
            </div>

            <Pager {...view} onChange={goToPage} onHover={prefetchPage} />
        </>
    );
}

export function Video() {
    const { token, user, subStatus, subPending, openLogin, requireSub } = useSession();
    const paid = subStatus === 'active';
    const navigate = useNavigate();
    const [params, setParams] = useSearchParams();

    const [phase, setPhase] = useState('grid'); // 'grid' | 'pick'
    const [popupSound, setPopupSound] = useState(false);
    const [selectedTemplate, setSelectedTemplate] = useState(null);
    const [selectedImage, setSelectedImage] = useState(null);
    const [selectedImageFull, setSelectedImageFull] = useState(null);
    const [showImagePicker, setShowImagePicker] = useState(false);
    const [showTemplatePicker, setShowTemplatePicker] = useState(false);

    const [video, setVideo] = useState(null);
    const [genType, setGenType] = useState(null);
    const [generated, setGenerated] = useState(false);
    const [taskId, setTaskId] = useState(null);
    const [gallery, setGallery] = useState([]);
    const [photos, setPhotos] = useState([]);

    const resultRef = useRef(null);
    const gridRef = useRef(null);
    // The floating "Generating" pill is only useful once the progress panel is off screen.
    const resultInView = useInViewport(resultRef, '0px 0px -120px 0px');

    // The preview popup lives in the URL (?t=<id>) so scenes are linkable and Back closes it.
    const popupTemplate = templateById(params.get('t'));
    const setPopup = useCallback((tpl, replace = false) => {
        const next = new URLSearchParams(params);
        if (tpl) next.set('t', tpl.id); else next.delete('t');
        setParams(next, { replace });
    }, [params, setParams]);

    const { handleAnimate, error } = useVideoAnimate({
        token, user, selectedImage,
        action: selectedTemplate?.action || null,
        callbacks: {
            setGenType, setGenerated, setVideo, setTaskId,
            setShowLoginPopup: openLogin,
            onScrollToResults: () => resultRef.current?.scrollIntoView({ behavior: 'smooth' }),
        },
    });

    // Full-quality version of the picked photo for the big pick card.
    useEffect(() => {
        setSelectedImageFull(null);
        if (!selectedImage?.mediaId || !token) return;
        fetchRemixData(token, selectedImage.mediaId).then((data) => {
            if (data?.url) setSelectedImageFull(data.url);
        }).catch(() => {});
    }, [selectedImage?.mediaId, token]);

    useEffect(() => {
        if (!token) return;
        fetchGallery([], 0, false, token, false, false, true, 200).then((data) => {
            if (Array.isArray(data?.content)) setPhotos(data.content);
        }).catch(() => {});
    }, [token]);

    useEffect(() => {
        if (!token) return;
        fetchGallery([], 0, true, token, false, false, true, 200).then((data) => {
            if (Array.isArray(data?.content)) setGallery(data.content.map((i) => ({ ...i, is_video: true })));
        }).catch(() => {});
    }, [token, generated]);

    const openTemplate = (tpl) => {
        setPopupSound(isSoundOwner(`card:${tpl.id}`));
        setPopup(tpl);
    };

    const handleProceed = () => requireSub(() => {
        if (popupTemplate?.status !== 'live') return; // a fresh subscriber now sees the soon state instead
        setSelectedTemplate(popupTemplate);
        setSelectedImage(null);
        setPopup(null, true);
        setPhase('pick');
        window.scrollTo({ top: 0, behavior: 'smooth' });
    });

    const resetToGrid = () => {
        setPhase('grid');
        setSelectedTemplate(null);
        setSelectedImage(null);
        setVideo(null);
        setGenerated(false);
        setGenType(null);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const handleGenerate = () => requireSub(() => { if (selectedImage) handleAnimate(); });

    const showLoading = !!genType && !generated;
    const showResult = !!video && generated;
    const selectedMedia = selectedTemplate ? resolveMedia(selectedTemplate) : null;

    return (
        <div className="vidPage">
            {phase === 'grid' && (
                <section className="container">
                    <div className="hero">
                        <span className="heroPill"><span className="dot" /> AI video · {VIDEO_TEMPLATES.length} scenes</span>
                        <h1 className="heroTitle">
                            <span className="heroTitleLead"><HeroDrop />Undress</span> <em className="gradText">AI girl.</em>
                        </h1>
                    </div>
                    <TemplateBrowser onOpen={openTemplate} paid={paid} subPending={subPending} gridRef={gridRef} />
                </section>
            )}

            {phase === 'pick' && selectedTemplate && (
                <section className="container vidPick">
                    <button type="button" className="btn btn--glass btn--sm vidPickBack" onClick={resetToGrid}>
                        <Icon name="arrowLeft" size={15} /> All scenes
                    </button>

                    <ol className="stepDots">
                        <li className={selectedImage ? 'isDone' : 'isActive'}><span>1</span>Image</li>
                        <li className="isDone"><span>2</span>Scene</li>
                        <li className={selectedImage ? 'isActive' : ''}><span>3</span>Generate</li>
                    </ol>

                    <div className="vidPickCards">
                        <button type="button" className={`vidPickCard${selectedImage ? ' isPicked' : ''}`} onClick={() => setShowImagePicker(true)}>
                            {selectedImage ? (
                                <>
                                    <img className="vidPickMedia" src={selectedImageFull || selectedImage.url} alt="" />
                                    <span className="vidPickScrim" />
                                    <span className="vidPickMeta"><b>Image</b><span className="vidPickChange">Change</span></span>
                                </>
                            ) : (
                                <span className="vidPickEmpty">
                                    <span className="vidPickPlus"><Icon name="plus" size={26} strokeWidth={2.4} /></span>
                                    <b>Add an image</b>
                                    <span>Sample or one of your photos</span>
                                </span>
                            )}
                        </button>

                        <button type="button" className="vidPickCard isPicked" onClick={() => setShowTemplatePicker(true)}>
                            <video className="vidPickMedia" src={selectedMedia.preview} poster={selectedMedia.poster} autoPlay loop muted playsInline />
                            <span className="vidPickScrim" />
                            <span className="vidPickMeta"><b>{selectedTemplate.title}</b><span className="vidPickChange">Change</span></span>
                        </button>
                    </div>

                    <div className="vidPickCta">
                        {generated ? (
                            <button type="button" className="btn btn--primary btn--lg btn--block" onClick={resetToGrid}>Create new</button>
                        ) : (
                            <button type="button" className="btn btn--primary btn--lg btn--block" onClick={handleGenerate} disabled={!selectedImage || showLoading}>
                                <Icon name="play" size={15} /> {showLoading ? 'Generating…' : 'Generate video'}
                            </button>
                        )}
                        {!generated && !showLoading && (
                            selectedImage
                                ? <p className="vidPickHint">~2 min render · HD · No watermark</p>
                                : <p className="vidPickHint vidPickHint--missing"><Icon name="arrowUp" size={13} /> Add an image to continue</p>
                        )}
                        {error && <div className="note note--error">{error}</div>}
                    </div>
                </section>
            )}

            <div ref={resultRef} className="vidResultAnchor" />
            {(showLoading || showResult) && (
                <section className="container vidResult">
                    {showLoading ? (
                        <GenerationProgress
                            durationMs={RENDER_MS}
                            title={selectedTemplate?.title}
                            messages={RENDER_MESSAGES}
                            tip="Don't close this tab — your video is rendering"
                        />
                    ) : (
                        <div className="vidResultCard glass">
                            <div className="vidResultHead">
                                <span className="heroPill"><span className="dot dot--success" /> Ready</span>
                                <span className="sectionMeta">{selectedTemplate?.title || 'Video'} · just now</span>
                            </div>
                            <video className="vidResultVideo" src={video} autoPlay loop muted playsInline controls />
                            <div className="vidResultActions">
                                <button type="button" className="btn btn--primary" onClick={() => downloadFile(video)}>
                                    <Icon name="download" size={16} /> Download
                                </button>
                                <button type="button" className="btn btn--glass" onClick={resetToGrid}>
                                    <Icon name="plus" size={16} /> Create another
                                </button>
                            </div>
                            {taskId && (
                                <Link className="vidReportLink" to={`/report?media_id=${encodeURIComponent(taskId)}`}>
                                    <Icon name="flag" size={13} /> Report content
                                </Link>
                            )}
                        </div>
                    )}
                </section>
            )}

            {user && <GalleryStrip items={gallery} token={token} kind="video" emptyHint="Pick a scene above to get started" />}

            <TemplatePreviewPopup
                template={phase === 'grid' ? popupTemplate : null}
                paid={paid}
                startWithSound={popupSound}
                onClose={() => setPopup(null)}
                onGenerate={handleProceed}
                onOpenTemplate={(tpl) => setPopup(tpl, true)}
                onBrowseLive={() => setParams({ tag: 'live' })}
            />
            <TemplatePickerPopup
                isOpen={showTemplatePicker}
                onClose={() => setShowTemplatePicker(false)}
                selectedId={selectedTemplate?.id}
                onSelect={(tpl) => {
                    setSelectedTemplate(tpl);
                    // The sample image belongs to the scene, so it follows the scene.
                    setSelectedImage((img) => (img?.type === 'sample' ? { ...img, url: resolveMedia(tpl).sample } : img));
                }}
            />
            <ImagePickerPopup
                isOpen={showImagePicker}
                onClose={() => setShowImagePicker(false)}
                sampleUrl={selectedMedia?.sample}
                photos={photos}
                selectedImage={selectedImage}
                onSelect={setSelectedImage}
                onCreatePhoto={() => navigate('/photo')}
            />

            {showLoading && selectedTemplate && !resultInView && (
                <button type="button" className="vidNowPill glass" onClick={() => resultRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' })}>
                    <span className="dot dot--pulse" /> Generating <b>{selectedTemplate.title}</b>
                </button>
            )}
        </div>
    );
}
